import { useCallback, useEffect, useRef, useState } from "react";
import type { ReasoningEffort } from "@/types/chat.type";
import type { WorkshopQuickCreateRef } from "@/types/workshop.type";
import type { WorkshopBootstrapPayload } from "./WorkshopStartComposer";
import { useWorkshopProject } from "../api/useWorkshopProjects";
import { useWorkshopProjectRoom } from "../api/useWorkshopProjectRoom";
import type { WorkshopOutput } from "../workshopOutputs";
import WorkshopChatPanel, { type WorkshopChatPanelHandle } from "./WorkshopChatPanel";
import WorkshopCouldNotOpen from "./WorkshopCouldNotOpen";
import WorkshopProjectHome from "./WorkshopProjectHome";
import WorkshopLane from "./WorkshopLane";
import { ChevronLeft, PanelRight } from "lucide-react";
import { isNetworkError } from "./WorkshopParts";
import WorkshopSidePanel, {
  type PaneState,
  type WorkshopSidePanelHandle,
} from "./WorkshopSidePanel";
import type { RoomSection } from "./WorkshopThreadHead";

const CLOSED_PANE: PaneState = { open: false, docked: false, section: null, expanded: false };

type Props = {
  brandId: string;
  projectId: string;
  /** The thread the URL asks for. `new` or a seed = a blank thread, not the latest. */
  threadId?: string | null;
  /** The chat moved to another thread (or a new, unsent one: null). */
  onThreadChange?: (threadId: string | null) => void;
  /** First message from the draft surface, sent once the project chat is ready. */
  seedMessage?: string | null;
  /** Model/effort chosen on the draft surface; it must survive into the new session. */
  seedModelId?: string | null;
  seedReasoningEffort?: ReasoningEffort | null;
  /** The skills and agents the first message starts (its pills). */
  seedQuickCreates?: WorkshopQuickCreateRef[] | null;
  onSeedConsumed?: () => void;
  /** Files picked on the opening composer. Uploaded once this thread is open. */
  seedFiles?: File[] | null;
  seedThreadId?: string | null;
  onSeedFilesConsumed?: () => void;
  /** The project's home (its chats and a composer) instead of a chat. */
  home?: boolean;
  onStartThread: (payload: WorkshopBootstrapPayload) => Promise<void>;
  onStartWithFiles: (files: File[]) => void;
  onOpenThread: (threadId: string) => void;
  /** Share this project (or see who is in it). */
  onOpenPeople?: () => void;
  onBackToProjects: () => void;
  /** An output picked on the Projects home: open it in the room once it is up. */
  pendingOutput?: WorkshopOutput | null;
  onPendingOutputConsumed?: () => void;
  /** Phone: sheets for the model menu, attach, and the project panel. */
  isPhone?: boolean;
  /** Compact shell: the title capsule opens the sidebar sheet. */
  lane?: boolean;
  onOpenSidebar?: () => void;
  /** The open thread was deleted or left from inside it: move off it. */
  onThreadGone?: (threadId: string) => void;
};

export default function WorkshopProjectView({
  brandId,
  projectId,
  threadId = null,
  onThreadChange,
  seedMessage = null,
  seedModelId = null,
  seedReasoningEffort = null,
  seedQuickCreates = null,
  onSeedConsumed,
  seedFiles = null,
  seedThreadId = null,
  onSeedFilesConsumed,
  home = false,
  onStartThread,
  onStartWithFiles,
  onOpenThread,
  onOpenPeople,
  onBackToProjects,
  pendingOutput = null,
  onPendingOutputConsumed,
  isPhone = false,
  lane = false,
  onOpenSidebar,
  onThreadGone,
}: Props) {
  const projectQuery = useWorkshopProject(projectId);
  const project = projectQuery.data?.project ?? null;
  const canWrite = projectQuery.data?.canWrite ?? false;
  const sidePanelRef = useRef<WorkshopSidePanelHandle>(null);
  const chatPanelRef = useRef<WorkshopChatPanelHandle>(null);
  const [pane, setPane] = useState<PaneState>(CLOSED_PANE);
  // What collaborators change here arrives as it happens.
  useWorkshopProjectRoom(projectId);

  const handlePaneState = useCallback((state: PaneState) => setPane(state), []);

  // The side panel drafts a message (e.g. "expand this idea into a brief");
  // the chat owns the composer, so it lands there for the user to send.
  const handleDraftInChat = useCallback((message: string) => {
    chatPanelRef.current?.prefillComposer(message);
  }, []);

  // A document's Rewrite: the passage goes into the chat's composer as a
  // quote, and the person says what they want.
  const handleQuoteInChat = useCallback((passage: string) => {
    chatPanelRef.current?.quoteInComposer(passage);
  }, []);

  // "Saved … in <thread>": the viewer's own thread opens in the chat.
  const handleOpenThread = useCallback((id: string) => onOpenThread(id), [onOpenThread]);

  // The chat already shows the deliverable; this only nudges the panel for a
  // user whose eyes were elsewhere on the page.
  const handleArtifactWritten = useCallback((artifactId: string | null) => {
    sidePanelRef.current?.flash("artifacts", artifactId);
  }, []);

  const handleOpenArtifact = useCallback((artifactId: string) => {
    sidePanelRef.current?.focusArtifact(artifactId);
  }, []);

  const handleOpenIdea = useCallback((ideaId: string) => {
    sidePanelRef.current?.focusIdea(ideaId);
  }, []);

  const handleIdeaSaved = useCallback((ideaId: string) => {
    sidePanelRef.current?.flash("ideas", ideaId);
  }, []);

  const handleContextWritten = useCallback((contextItemId: string | null) => {
    sidePanelRef.current?.flash("context", contextItemId);
  }, []);

  const handleDeploymentWritten = useCallback((deploymentId: string | null) => {
    sidePanelRef.current?.flash("agents", deploymentId);
  }, []);

  const handleMediaWritten = useCallback((mediaId: string | null) => {
    sidePanelRef.current?.flash("media", mediaId);
  }, []);

  const handleFileWritten = useCallback((documentId: string) => {
    sidePanelRef.current?.flash("context", documentId);
  }, []);

  // A file or image the user attached in the chat lands in the room, never in
  // a preview. Images go to Media, every other file to Sources.
  const handleUploadAdded = useCallback((tab: "media" | "context", itemId: string) => {
    sidePanelRef.current?.reveal(tab, itemId);
  }, []);

  const handleOpenMedia = useCallback((mediaId: string) => {
    sidePanelRef.current?.focusMedia(mediaId);
  }, []);

  // The thread head's room entries open the pane at their section, or close
  // it when that section is already showing.
  const handleOpenRoomSection = useCallback((section: RoomSection) => {
    sidePanelRef.current?.toggleRoom(section);
  }, []);

  const openOutput = useCallback((output: WorkshopOutput) => {
    const panel = sidePanelRef.current;
    if (!panel) return false;
    if (output.kind === "document") panel.focusArtifact(output.id);
    else if (output.kind === "idea") panel.focusIdea(output.id);
    else panel.focusMedia(output.id);
    return true;
  }, []);

  // An output picked on the Projects home opens once the room has mounted
  // (the shell renders it a frame or two after the project loads).
  useEffect(() => {
    if (!pendingOutput || !project) return;
    let frame = 0;
    let tries = 0;
    const attempt = () => {
      if (openOutput(pendingOutput) || tries > 30) {
        onPendingOutputConsumed?.();
        return;
      }
      tries += 1;
      frame = window.requestAnimationFrame(attempt);
    };
    attempt();
    return () => window.cancelAnimationFrame(frame);
  }, [pendingOutput, project, openOutput, onPendingOutputConsumed]);

  const archived = project?.status === "archived";

  if (projectQuery.isLoading) {
    return <LandingSkeleton />;
  }

  if (projectQuery.isError || !project) {
    return (
      <WorkshopCouldNotOpen
        unreachable={projectQuery.isError && isNetworkError(projectQuery.error)}
        onRetry={() => void projectQuery.refetch()}
        onBack={onBackToProjects}
      />
    );
  }

  // The split desk: the chat (or the landing) and, beside it, the pane.
  return (
    <div
      className="ws-desk"
      data-pane={pane.docked ? (pane.expanded ? "expanded" : "open") : undefined}
    >
      <div className="ws-desk-main">
      {home && projectQuery.data && lane ? (
        <WorkshopLane
          icon={ChevronLeft}
          label="Projects"
          onCapsule={onBackToProjects}
          action={{ icon: PanelRight, label: "Project panel", onPress: () => sidePanelRef.current?.openLibrary() }}
        />
      ) : null}
      {home && projectQuery.data ? (
        <WorkshopProjectHome
          detail={projectQuery.data}
          canWrite={canWrite}
          onStart={onStartThread}
          onStartWithFiles={onStartWithFiles}
          onOpenThread={onOpenThread}
          onShare={() => onOpenPeople?.()}
        />
      ) : (
      <WorkshopChatPanel
        key={projectId}
        ref={chatPanelRef}
        brandId={brandId}
        projectId={projectId}
        projectName={project.name}
        requestedThreadId={threadId}
        onThreadChange={onThreadChange}
        archived={archived}
        canWrite={canWrite}
        onArtifactWritten={handleArtifactWritten}
        onOpenArtifact={handleOpenArtifact}
        onOpenIdea={handleOpenIdea}
        onIdeaSaved={handleIdeaSaved}
        onContextWritten={handleContextWritten}
        onDeploymentWritten={handleDeploymentWritten}
        onMediaWritten={handleMediaWritten}
        onFileWritten={handleFileWritten}
        onUploadAdded={handleUploadAdded}
        onOpenMedia={handleOpenMedia}
        onOpenLibrary={handleOpenRoomSection}
        libraryOpen={pane.open}
        openRoomSection={pane.section}
        isPhone={isPhone}
        lane={lane}
        seedMessage={seedMessage}
        seedModelId={seedModelId}
        seedReasoningEffort={seedReasoningEffort}
        seedQuickCreates={seedQuickCreates}
        onSeedConsumed={onSeedConsumed}
        seedFiles={seedFiles}
        seedThreadId={seedThreadId}
        onSeedFilesConsumed={onSeedFilesConsumed}
        onManageProject={project.isOwner ? onOpenPeople : undefined}
        onOpenProjectSheet={lane ? onOpenSidebar : undefined}
        onThreadGone={onThreadGone}
      />
      )}
      </div>
      <WorkshopSidePanel
        key={projectId}
        ref={sidePanelRef}
        brandId={brandId}
        projectId={projectId}
        canWrite={canWrite && !archived}
        isPhone={isPhone}
        onStateChange={handlePaneState}
        onDraftInChat={handleDraftInChat}
        onQuoteInChat={handleQuoteInChat}
        onOpenThread={handleOpenThread}
      />
    </div>
  );
}

/** The landing's shape while the project loads: title, lines, composer, tiles. */
function LandingSkeleton() {
  return (
    <div className="ws-landing-skel" aria-busy="true" aria-label="Loading the project">
      <span className="ws-skel ws-skel--display" />
      <span className="ws-skel ws-skel--line" style={{ width: "min(560px, 90%)" }} />
      <span className="ws-skel ws-skel--line" style={{ width: "min(380px, 70%)" }} />
      <span className="ws-skel ws-skel--composer" />
      <div className="ws-made-grid" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span key={i} className="ws-made-tile ws-made-tile--skel">
            <span className="ws-skel ws-skel--tag" />
            <span className="ws-skel ws-skel--line" style={{ width: "70%" }} />
          </span>
        ))}
      </div>
    </div>
  );
}
